import React, { useState, useEffect } from 'react';
import { buttonClass } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import useScheduleStore from '../stores/useScheduleStore';
import { useThemeStore } from '../stores/useThemeStore';
import { OOOEntry, ScheduleStaff } from './types';

interface OOOModalProps {
  isOpen: boolean;
  onClose: () => void;
  editEntry?: OOOEntry | null;
  preselectedUserId?: string;
  staff: ScheduleStaff[];
  isProducer: boolean; // if true, shows user picker
}

const OOOModal: React.FC<OOOModalProps> = ({
  isOpen, onClose, editEntry, preselectedUserId, staff, isProducer,
}) => {
  const { addOOO, updateOOO, deleteOOO } = useScheduleStore();
  const confirm = useConfirm();
  const dayMode = useThemeStore((st) => st.dayMode);
  const [userId, setUserId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isTentative, setIsTentative] = useState(false);
  const [enteredInOnpay, setEnteredInOnpay] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editEntry) {
      setUserId(editEntry.user_id);
      setStartDate(editEntry.start_date.slice(0, 10));
      setEndDate(editEntry.end_date.slice(0, 10));
      setIsTentative(editEntry.is_tentative);
      setEnteredInOnpay(editEntry.entered_in_onpay);
      setNotes(editEntry.notes || '');
    } else {
      setUserId(preselectedUserId || (staff[0]?.id || ''));
      setStartDate('');
      setEndDate('');
      setIsTentative(false);
      setEnteredInOnpay(false);
      setNotes('');
    }
  }, [editEntry, preselectedUserId, staff, isOpen]);

  // The fields as the effect above seeds them, for telling typed work from an untouched dialog.
  const dirty = isOpen && (editEntry
    ? userId !== editEntry.user_id ||
      startDate !== editEntry.start_date.slice(0, 10) ||
      endDate !== editEntry.end_date.slice(0, 10) ||
      isTentative !== editEntry.is_tentative ||
      enteredInOnpay !== editEntry.entered_in_onpay ||
      notes !== (editEntry.notes || '')
    : userId !== (preselectedUserId || staff[0]?.id || '') ||
      startDate !== '' ||
      endDate !== '' ||
      isTentative ||
      enteredInOnpay ||
      notes !== '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate || !userId) return;
    setSubmitting(true);

    try {
      if (editEntry) {
        await updateOOO(editEntry.id, {
          start_date: startDate,
          end_date: endDate,
          is_tentative: isTentative,
          entered_in_onpay: enteredInOnpay,
          notes,
        });
      } else {
        await addOOO({
          user_id: userId,
          start_date: startDate,
          end_date: endDate,
          is_tentative: isTentative,
          entered_in_onpay: enteredInOnpay,
          notes,
        } as any);
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editEntry) return;
    if (!(await confirm({ message: 'Delete this OOO entry?', confirmLabel: 'Delete', tone: 'danger' }))) return;
    setSubmitting(true);
    try {
      await deleteOOO(editEntry.id);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <>
      {editEntry && (
        <button
          type="button"
          onClick={handleDelete}
          disabled={submitting}
          className={`${buttonClass('danger', 'md')} order-first`}
        >
          Delete
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        className={buttonClass('quiet', 'md')}
      >
        Cancel
      </button>
      <button
        type="submit"
        form="ooo-form"
        disabled={submitting || !startDate || !endDate || !userId}
        className={buttonClass('filled', 'md')}
      >
        {submitting ? 'Saving...' : editEntry ? 'Update' : 'Add OOO'}
      </button>
    </>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={editEntry ? 'Edit OOO' : 'Add Out of Office'}
      size="md"
      light={dayMode}
      busy={submitting}
      dirty={dirty}
      footer={footer}
    >
      {/* The dialog is portalled out of the page's day-mode wrapper, so the fields carry it themselves. */}
      <div className={dayMode ? 'day-mode' : undefined}>
        <form id="ooo-form" onSubmit={handleSubmit} className="space-y-4">
          {/* User picker (producers only) */}
          {isProducer && !editEntry && (
            <div>
              <label className="block text-sm text-on-surface-variant day:text-gray-700 mb-1">Staff Member</label>
              <select
                value={userId}
                onChange={e => setUserId(e.target.value)}
                className="w-full bg-surface-container-high shadow-recess day:bg-white day:border day:border-gray-300 rounded px-3 py-2 text-sm text-on-surface day:text-gray-900
                  focus:outline-none focus:ring-2 focus:ring-primary/70"
              >
                {staff.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.firstname} {s.lastname}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-on-surface-variant day:text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                required
                className="w-full bg-surface-container-high shadow-recess day:bg-white day:border day:border-gray-300 rounded px-3 py-2 text-sm text-on-surface day:text-gray-900
                  focus:outline-none focus:ring-2 focus:ring-primary/70"
              />
            </div>
            <div>
              <label className="block text-sm text-on-surface-variant day:text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                min={startDate}
                required
                className="w-full bg-surface-container-high shadow-recess day:bg-white day:border day:border-gray-300 rounded px-3 py-2 text-sm text-on-surface day:text-gray-900
                  focus:outline-none focus:ring-2 focus:ring-primary/70"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-on-surface day:text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={isTentative}
                onChange={e => setIsTentative(e.target.checked)}
                className="rounded text-primary focus:ring-primary"
              />
              Tentative
            </label>
            <label className="flex items-center gap-2 text-sm text-on-surface day:text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={enteredInOnpay}
                onChange={e => setEnteredInOnpay(e.target.checked)}
                className="rounded text-primary focus:ring-primary"
              />
              Entered in OnPay
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-on-surface-variant day:text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full bg-surface-container-high shadow-recess day:bg-white day:border day:border-gray-300 rounded px-3 py-2 text-sm text-on-surface day:text-gray-900
                focus:outline-none focus:ring-2 focus:ring-primary/70 resize-none"
              placeholder="Reason, etc."
            />
          </div>
        </form>
      </div>
    </Dialog>
  );
};

export default OOOModal;
